import FavoritesStorage from "@/modules/FavoritesStorage";
import type ExperienceData from "@/modules/ExperienceData";
import type { ExperienceId } from "@/types/experience";

export default class FavoritesData {
  declare experienceData: ExperienceData;
  declare favoritesStorage: FavoritesStorage;
  declare favoriteIndex: number;
  declare imageIndex: number;
  // Set after _reconcileFavorites runs, so that call reads an undefined cluster.
  declare _cluster: string;

  constructor(experienceData: ExperienceData) {
    this.experienceData = experienceData;
    this.favoritesStorage = new FavoritesStorage();
    this.favoriteIndex = 0;
    this.imageIndex = 0;
    this._reconcileFavorites();
    this._cluster = "fav";
  }

  getFavoritesCount() {
    let ret = 0;
    if (this.experienceData.idexperience) {
      const favorites = this.favoritesStorage.getFavorites(
        this.experienceData.idexperience,
        this._cluster,
      );
      if (typeof favorites !== "undefined") {
        ret = favorites.length;
      }
    }
    return ret;
  }

  getFavorites(): ExperienceId[] {
    let ret: ExperienceId[] | undefined;
    if (this.experienceData.idexperience) {
      const favorites = this.favoritesStorage.getFavorites(
        this.experienceData.idexperience,
        this._cluster,
      );
      if (typeof favorites !== "undefined") {
        ret = favorites;
      }
    }

    if (typeof ret === "undefined") {
      ret = [];
    }

    return ret;
  }

  getFavorite(index?: number) {
    const favorites = this.getFavorites();
    if (typeof index === "undefined") {
      index = this.favoriteIndex;
    }
    if (index < 0 || index >= favorites.length) {
      return null;
    } else {
      return favorites[index];
    }
  }

  getFavoriteArticles() {
    const ret = [];
    const favorites = this.getFavorites();
    for (let n = 0; n < favorites.length; n++) {
      ret.push(this.getFavoriteArticle(n));
    }
    return ret;
  }

  getFavoriteArticle(index?: number) {
    const favorites = this.getFavorites();
    if (typeof index === "undefined") {
      index = this.favoriteIndex;
    }
    if (index < 0 || index >= favorites.length) {
      return null;
    }

    const id = favorites[index];
    return this.experienceData.getArticleById(id);
  }

  getFavoriteArticleImages(index?: number) {
    return this.experienceData.getImagesFromArticle(
      this.getFavoriteArticle(index),
    );
  }

  isFavorite(id: ExperienceId) {
    const favorites = this.getFavorites();
    return favorites.indexOf(id) >= 0;
  }

  selectFavorite(index: number) {
    const favorites = this.getFavorites();
    let ret = false;
    if (
      index >= 0 &&
      index < favorites.length &&
      this.favoriteIndex !== index
    ) {
      this.favoriteIndex = index;
      this._resetImageIndex();
      ret = true;
    }

    return ret;
  }

  selectFavoriteImage(index: number) {
    const images = this.getFavoriteArticleImages();
    let ret = false;
    if (images && index >= 0 && index < images.length) {
      this.imageIndex = index;
      ret = true;
    }

    return ret;
  }

  addFavorite(id: ExperienceId) {
    let ret = false;
    if (this.experienceData.idexperience && !this.isFavorite(id)) {
      ret = true;
      this.favoritesStorage.addFavorite(
        this.experienceData.idexperience,
        this._cluster,
        id,
      );
      this.favoritesStorage.save();
      this._adjustFavoriteIndex();
    }
    return ret;
  }

  addFavorites(ids: ExperienceId[]) {
    let ret = false;
    if (this.experienceData.idexperience) {
      ret = true;
      this.favoritesStorage.addFavorites(
        this.experienceData.idexperience,
        this._cluster,
        ids,
      );
      this.favoritesStorage.save();
      this._adjustFavoriteIndex();
    }
    return ret;
  }

  removeFavorite(id: ExperienceId) {
    let ret = false;
    if (typeof this.experienceData.idexperience !== "undefined") {
      ret = this.favoritesStorage.removeFavorite(
        this.experienceData.idexperience,
        this._cluster,
        id,
      );
      this.favoritesStorage.save();
      this._adjustFavoriteIndex();
    }
    return ret;
  }

  removeFavorites(ids: ExperienceId[]) {
    let ret = false;
    if (this.experienceData.idexperience) {
      ret = this.favoritesStorage.removeFavorites(
        this.experienceData.idexperience,
        this._cluster,
        ids,
      );
      this.favoritesStorage.save();
      this._adjustFavoriteIndex();
    }
    return ret;
  }

  addAll() {
    let ret = false;
    if (this.experienceData.articles) {
      for (let n = 0, max = this.experienceData.articles.length; n < max; n++) {
        ret = this.addFavorite(this.experienceData.articles[n].id) || ret;
      }
    }
    return ret;
  }

  removeAll() {
    let ret = false;
    if (typeof this.experienceData.idexperience !== "undefined") {
      ret = this.favoritesStorage.clearFavorites(
        this.experienceData.idexperience,
        this._cluster,
      );
      this.favoritesStorage.save();
      this._adjustFavoriteIndex();
    }
    return ret;
  }

  removeFavoriteAtIndex(index: number) {
    let ret = false;
    if (typeof this.experienceData.idexperience !== "undefined") {
      ret = this.favoritesStorage.removeFavoriteAtIndex(
        this.experienceData.idexperience,
        this._cluster,
        index,
      );
      this.favoritesStorage.save();
      this._adjustFavoriteIndex();
    }
    return ret;
  }

  incrementFavoriteIndex() {
    let ret = false;
    const favorites = this.getFavorites();
    const count = favorites ? favorites.length : 0;
    if (this.favoriteIndex < count - 1) {
      this.favoriteIndex += 1;
      this._resetImageIndex();
      ret = true;
    }
    return ret;
  }

  decrementFavoriteIndex() {
    let ret = false;
    if (this.favoriteIndex > 0) {
      this.favoriteIndex -= 1;
      this._resetImageIndex();
      ret = true;
    }
    return ret;
  }

  incrementFavoriteImageIndex() {
    let ret = false;
    const images = this.getFavoriteArticleImages();
    const count = images ? images.length : 0;
    if (this.imageIndex < count - 1) {
      this.imageIndex += 1;
      ret = true;
    }
    return ret;
  }

  decrementFavoriteImageIndex() {
    let ret = false;
    if (this.imageIndex > 0) {
      this.imageIndex -= 1;
      ret = true;
    }
    return ret;
  }

  _reconcileFavorites() {
    // getFavorites() is empty without an idexperience.
    const idexperience = this.experienceData.idexperience;
    if (!idexperience) {
      return;
    }
    const favorites = this.getFavorites();
    let updated = false;
    for (let n = favorites.length - 1; n >= 0; n--) {
      const article = this.experienceData.getArticleById(favorites[n]);
      if (article === null) {
        updated =
          this.favoritesStorage.removeFavorite(
            idexperience,
            this._cluster,
            favorites[n],
          ) || updated;
      }
    }
    if (updated) {
      this.favoritesStorage.save();
    }
  }

  _adjustFavoriteIndex() {
    const favorites = this.getFavorites();
    if (this.favoriteIndex < 0 && favorites.length > 0) {
      this.favoriteIndex = 0;
    } else if (this.favoriteIndex >= favorites.length) {
      this.favoriteIndex = favorites.length - 1;
    }
  }

  _resetImageIndex() {
    const images = this.getFavoriteArticleImages();
    this.imageIndex = images && images.length > 0 ? 0 : -1;
  }
}
